import { useState } from 'react'
import { FileSignature, Info, Plus, Send } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useESignMutations, useESignProviders, useEnvelopes } from '../api'

/**
 * Documents out for signature.
 *
 * <p>The adapter that exists is the manual one, and the screen says exactly what it will do
 * rather than implying a vendor is a setting away. What is real here is the part that matters
 * in a dispute: who signed, in what order, when, and what was filed afterwards.
 */
export function SignaturesPage() {
  useBreadcrumb()
  const envelopes = useEnvelopes()
  const providers = useESignProviders()
  const actions = useESignMutations()

  const [draft, setDraft] = useState<{
    subject: string; message: string
    signers: { fullName: string; email: string; signingOrder: number }[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  const run = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setBanner(null)
      try {
        await work()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Signatures"
        subtitle="What is out for signature, who it is waiting on, and what came back."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                subject: '', message: '',
                signers: [{ fullName: '', email: '', signingOrder: 1 }],
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New envelope
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      {providers.data?.map((provider) => (
        <p key={provider.kind} className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            <span className="font-mono text-xs">{provider.kind}</span> — {provider.describes}
            {provider.kind === 'MANUAL' && (
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                A vendor adapter — DocuSign, Adobe, Zoho — is a class implementing the provider
                interface plus credentials. None is shipped, because one that has never run
                against a real account is worse than none at all.
              </span>
            )}
          </span>
        </p>
      ))}

      <Card>
        {envelopes.isPending && <Skeleton className="m-4 h-24" />}
        {envelopes.isError && (
          <ErrorState error={envelopes.error} onRetry={() => void envelopes.refetch()} />
        )}
        {envelopes.isSuccess && envelopes.data.length === 0 && (
          <EmptyState
            title="Nothing out for signature"
            description="An envelope records who has to sign, in what order, and everything that happens to it."
          />
        )}
        {envelopes.isSuccess && envelopes.data.length > 0 && (
          <ul className="divide-y divide-[var(--color-border)]">
            {envelopes.data.map((envelope) => (
              <li key={envelope.id} className="p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]">
                      <FileSignature className="size-4" aria-hidden="true" />
                      {envelope.subject}
                    </p>
                    <p className="text-xs text-[var(--color-muted)]">{envelope.waitingOn}</p>
                    <p className="mt-1 text-xs text-[var(--color-muted)]">
                      {envelope.signers.map((signer) =>
                        `${signer.signingOrder}. ${signer.fullName} — ${signer.status.toLowerCase()}`).join(' · ')}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                      status={envelope.status}
                      tone={envelope.status === 'COMPLETED'
                        ? 'success'
                        : envelope.status === 'DECLINED' || envelope.status === 'VOIDED'
                          ? 'danger' : envelope.status === 'SENT' ? 'info' : 'neutral'}
                      label={envelope.status.charAt(0) + envelope.status.slice(1).toLowerCase()}
                    />
                    {envelope.status === 'DRAFT' && (
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => run(() => actions.send.mutateAsync(envelope.id),
                          'That envelope could not be sent')}
                      >
                        <Send className="size-4" aria-hidden="true" />
                        Send
                      </Button>
                    )}
                    {envelope.status === 'SENT' && (
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => {
                          const next = envelope.signers.find((signer) => signer.status === 'PENDING')
                          if (next) {
                            run(() => actions.sign.mutateAsync({
                              id: envelope.id, signerId: next.id,
                            }), 'That signature could not be recorded')
                          }
                        }}
                      >
                        Record the next signature
                      </Button>
                    )}
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => setOpen(open === envelope.id ? null : envelope.id)}
                    >
                      History
                    </Button>
                  </div>
                </div>

                {open === envelope.id && (
                  <ol className="mt-3 space-y-1 border-s border-[var(--color-border)] ps-3">
                    {envelope.history.map((event) => (
                      <li key={event.id} className="text-xs text-[var(--color-muted)]">
                        <DateValue value={event.occurredAt} withTime /> · {event.eventType}
                        {event.detail ? ` — ${event.detail}` : ''}
                        {event.actorName ? ` (${event.actorName})` : ''}
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(nowOpen) => { if (!nowOpen) { setDraft(null); setError(null) } }}
        title="New envelope"
        submitLabel="Create"
        loading={actions.create.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await actions.create.mutateAsync({
                subject: draft.subject,
                message: draft.message || null,
                provider: 'MANUAL',
                signers: draft.signers.map((signer, index) => ({
                  ...signer, signingOrder: index + 1,
                })),
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That envelope could not be created')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3">
            <div>
              <Label htmlFor="envelope-subject">What is being signed</Label>
              <Input
                id="envelope-subject" value={draft.subject}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="envelope-message">Message</Label>
              <Textarea
                id="envelope-message" rows={2} value={draft.message}
                onChange={(event) => setDraft({ ...draft, message: event.target.value })}
              />
            </div>
            <div>
              <Label>Signers, in the order they sign</Label>
              <div className="mt-1 space-y-2">
                {draft.signers.map((signer, index) => (
                  <div key={index} className="flex flex-wrap gap-2">
                    <Input
                      aria-label={`Signer ${index + 1} name`}
                      className="max-w-[14rem]"
                      placeholder="Full name"
                      value={signer.fullName}
                      onChange={(event) => setDraft({
                        ...draft,
                        signers: draft.signers.map((existing, position) =>
                          position === index
                            ? { ...existing, fullName: event.target.value } : existing),
                      })}
                    />
                    <Input
                      aria-label={`Signer ${index + 1} email`}
                      className="max-w-[16rem]"
                      placeholder="Email"
                      value={signer.email}
                      onChange={(event) => setDraft({
                        ...draft,
                        signers: draft.signers.map((existing, position) =>
                          position === index
                            ? { ...existing, email: event.target.value } : existing),
                      })}
                    />
                  </div>
                ))}
              </div>
              <Button
                type="button" variant="outline" size="sm" className="mt-2"
                onClick={() => setDraft({
                  ...draft,
                  signers: [...draft.signers,
                    { fullName: '', email: '', signingOrder: draft.signers.length + 1 }],
                })}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a signer
              </Button>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Order is enforced: a countersignature collected before the signature it
                counter-signs is not a countersignature.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
